import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ButtonIcon, Icon, Portal, Tooltip } from 'scala'
import { DataPlayer, useAudioEngine } from 'player'
import { usePanelAside } from 'player/src/modules/panel-aside/edits/store'
import { useUnmount } from 'react-use'
import styles from './view-instructions.module.scss'
import { usePersistentDismissible } from '../../../hooks/use-persistent-dismissable'
import { off, on } from '../../../lib/events'

interface Props {
  taskId: string
  posts: DataPlayer['posts']
}

export const ViewInstructions: React.FC<Props> = ({ taskId, posts }) => {
  const { i18n } = useLingui()
  const { showPosts, onShowPosts } = usePanelAside()
  const readyToPlay = useAudioEngine((p) => p.state.isReadyToPlay)
  const timeoutTooltip = useRef<any>(0)
  const [loaded, setLoaded] = useState(false)

  const { show: showPanelInstructions, onDismiss: onDismissPanelInstructions } =
    usePersistentDismissible({
      id: `instructions-${taskId}`,
      type: 'local',
      enabled: !!posts?.length
    })

  const { show: showTooltip, onDismiss: onDismissTooltipInstructions } =
    usePersistentDismissible({
      id: `instructions-tooltip-${taskId}`,
      type: 'local',
      enabled: !!posts?.length
    })

  const showTooltipInstructions = useMemo(
    () => showTooltip && !showPanelInstructions,
    [showTooltip, showPanelInstructions]
  )

  const onToggle = useCallback(() => {
    onShowPosts(!showPosts)
  }, [showPosts, onShowPosts])

  const handleClosePanelInstructions = useCallback(() => {
    onDismissPanelInstructions()
  }, [onDismissPanelInstructions])

  const handleCloseTooltipInstructions = useCallback(() => {
    if (showTooltipInstructions) {
      onDismissTooltipInstructions()
    }
  }, [showTooltipInstructions, onDismissTooltipInstructions])

  useEffect(() => {
    on('instructions:close', handleClosePanelInstructions)
    on('instructions:close-tooltip', handleCloseTooltipInstructions)

    return () => {
      off('instructions:close', handleClosePanelInstructions)
      off('instructions:close-tooltip', handleCloseTooltipInstructions)
    }
  }, [handleClosePanelInstructions, handleCloseTooltipInstructions])

  useEffect(() => {
    if (showPanelInstructions && !loaded && !showPosts && posts?.length) {
      setLoaded(true)
      onShowPosts(true)
    }
  }, [loaded, posts, showPosts, showPanelInstructions, onShowPosts])

  useEffect(() => {
    if (showTooltipInstructions && !timeoutTooltip.current) {
      timeoutTooltip.current = setTimeout(() => {
        handleCloseTooltipInstructions()
      }, 4000)
    }

    return () => {
      clearTimeout(timeoutTooltip.current)
    }
  }, [showTooltipInstructions, handleCloseTooltipInstructions])

  useUnmount(() => {
    if (showTooltipInstructions) {
      handleCloseTooltipInstructions()
    }
  })

  return (
    <div className={styles.container}>
      <ButtonIcon
        light
        onClick={onToggle}
        icon={<Icon name="notes" />}
        title={i18n._(t`branded_setlist_instructions_cta`)}
        disabled={!readyToPlay}
        className={styles.button}
      />

      {showPosts && (
        <div className={styles.active}>
          <Icon name="check-simple" />
        </div>
      )}

      {showTooltipInstructions && (
        <Portal id="player-actions-portal">
          <Tooltip
            nozzle
            topRight
            className={styles.tooltip}
            description={i18n._(t`branded_setlist_instructions_description`)}
          />
        </Portal>
      )}
    </div>
  )
}
